import type { DateModelType } from 'element-plus';
import type { Event } from '@patsnap/tahiti-core';
import type { ImageToTextResult } from '@/infrastructure/api-functions/core-drafting-api/create-novelty-check-task-api';

/**
 * 查新检索启动结果
 */
export interface NoveltySearchStartResult {
    /** 任务ID */
    taskId: string;
    /** 埋点数据 */
    trackingData: {
        /** 技术要点 */
        techEssential: string;
        /** 公开日期开始 */
        pbdStart: string;
        /** 公开日期结束 */
        pbdEnd: string;
    };
}

/**
 * 查新检索输入状态服务
 *
 * 负责管理查新检索输入界面的状态，包括输入文本、日期范围等，
 * 并提供创建查新任务的业务逻辑。
 */
export interface NoveltySearchInputState {
    /**
     * 获取当前输入文本
     */
    getInput(): string;

    /**
     * 设置输入文本
     * @param input - 输入文本
     */
    setInput(input: string): void;

    /**
     * 获取公开日期范围
     */
    getPublicationDate(): [DateModelType, DateModelType];

    /**
     * 设置公开日期范围
     * @param dates - 日期范围
     */
    setPublicationDate(dates: [DateModelType, DateModelType]): void;

    /**
     * 获取加载状态
     */
    isLoading(): boolean;

    /**
     * 获取图片转文本结果
     */
    getImageToTextResult(): ImageToTextResult | undefined;

    /**
     * 设置图片转文本结果
     * @param result - 图片转文本结果，传 undefined 清除
     */
    setImageToTextResult(result: ImageToTextResult | undefined): void;

    /**
     * 启动查新检索
     * 封装完整的启动流程，包括数据准备、任务创建等业务逻辑
     * @returns 启动结果，包含 taskId 和埋点数据
     */
    startNoveltySearch(): Promise<NoveltySearchStartResult>;

    /**
     * 重置所有状态
     */
    reset(): void;

    /**
     * 输入文本变化事件
     */
    readonly onInputChange: Event<string>;

    /**
     * 公开日期变化事件
     */
    readonly onPublicationDateChange: Event<[DateModelType, DateModelType]>;

    /**
     * 加载状态变化事件
     */
    readonly onLoadingChange: Event<boolean>;

    /**
     * 图片转文本结果变化事件
     */
    readonly onImageToTextResultChange: Event<ImageToTextResult | undefined>;
}

export const NoveltySearchInputState = Symbol('NoveltySearchInputState');
